/**
 * Shared utility functions for API response adapters.
 * All helpers are pure functions with no side-effects.
 */
import { bytesToBase64, base64ToBytes } from './document.adapter';
import type { EncryptedPayload } from '../../services/device-crypto.service';

/**
 * Extracts a `{ status, message }` envelope from an unknown API response.
 *
 * Used by every "fire-and-confirm" POST endpoint that returns:
 * `{ "status": "success", "message": "..." }`.
 * Both fields default to an empty string when absent or when `raw` is nullish.
 *
 * @param raw - Untyped API response (may be `null` or `undefined`).
 * @returns Object with `status` and `message` string fields, never `undefined`.
 *
 * @example
 * extractStatusMessage({ status: 'success', message: 'Done' });
 * // => { status: 'success', message: 'Done' }
 *
 * extractStatusMessage(null);
 * // => { status: '', message: '' }
 */
export function extractStatusMessage(raw: unknown): { status: string; message: string } {
  const r = raw as { status?: string; message?: string } | null | undefined;
  return {
    status: r?.status ?? '',
    message: r?.message ?? '',
  };
}

/**
 * Resolves a raw API value that is either a bare array or a `{ data: T[] }` envelope.
 *
 * Handles both Laravel paginated envelopes and plain arrays returned by reference
 * and geo endpoints.
 *
 * @typeParam T - The element type of the resolved array.
 * @param raw - Untyped API response: either `T[]` or `{ data?: T[] }`.
 * @returns The resolved array; returns `[]` when neither shape matches or input is nullish.
 *
 * @example
 * resolveList<{ city: string }>({ data: [{ city: 'Portland' }] });
 * // => [{ city: 'Portland' }]
 *
 * resolveList<{ city: string }>([{ city: 'Eugene' }]);
 * // => [{ city: 'Eugene' }]
 *
 * resolveList(null);
 * // => []
 */
export function resolveList<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  return ((raw as { data?: T[] } | null | undefined)?.data) ?? [];
}

/**
 * Unwraps the `data` property from a `{ data?: Record<string, unknown> }` envelope,
 * falling back to an empty object when the envelope or its `data` key is absent.
 *
 * Used by endpoints whose success response is a single keyed object rather than
 * an array (e.g. `hygiene-factor`, `reason`, `registeringtrouble`, `help-document`).
 *
 * @param raw - Untyped API response.
 * @returns The unwrapped `data` object, or `{}` when absent or nullish.
 *
 * @example
 * unwrapData({ data: { call_field_operation: '541-279-2521' } });
 * // => { call_field_operation: '541-279-2521' }
 *
 * unwrapData(null);
 * // => {}
 */
export function unwrapData(raw: unknown): Record<string, unknown> {
  return ((raw as { data?: Record<string, unknown> } | null | undefined)?.data) ?? {};
}

/**
 * Prefix marking a string as an {@link EncryptedPayload} produced by
 * {@link encodeEncryptedDocument} — lets {@link decodeEncryptedDocument}
 * distinguish it from the plain (device-unencrypted) value, which is what's
 * sent when the device-encryption step failed at enqueue time (see
 * `AffidavitAdapter.buildQueuedPayload()`'s own doc comment).
 *
 * @remarks
 * Lives here (adapter.utils.ts), not in `affidavit.adapter.ts`, specifically
 * to avoid a circular import: `AffidavitAdapter` already imports `UpqueueAdapter`
 * (for `transmitAll()`), and `UpqueueAdapter.transmitAffidavit()` needs this
 * same encode/decode pair — putting it in either adapter file would create an
 * import cycle between them.
 */
const ENCODED_DOCUMENT_PREFIX = 'devenc.v1:';

/**
 * Serialises an {@link EncryptedPayload} to a single string so
 * `affidavit_sign_document` can stay typed as `string` throughout — the wire
 * payload, the queued `Submission.payload`, and `AffidavitPayloadItem`'s own
 * type all already expect a string, and IndexedDB structured-clone support
 * for `Uint8Array` isn't needed here the way it is for
 * `DeviceCryptoService`'s other consumer (`SignatureStorageService`, which
 * stores the `EncryptedPayload` object directly).
 *
 * @param payload - The IV/ciphertext pair from `DeviceCryptoService.encrypt()`.
 */
export function encodeEncryptedDocument(payload: EncryptedPayload): string {
  return `${ENCODED_DOCUMENT_PREFIX}${bytesToBase64(payload.iv)}:${bytesToBase64(payload.ciphertext)}`;
}

/**
 * Inverse of {@link encodeEncryptedDocument}.
 *
 * @param encoded - The string previously produced by {@link encodeEncryptedDocument}.
 * @returns The decoded payload, or `null` when `encoded` doesn't carry the
 *          expected prefix/shape — callers should treat `null` as "this value
 *          was never device-encrypted in the first place" (see
 *          `UpqueueAdapter.transmitAffidavit()`), not as a corruption error.
 */
export function decodeEncryptedDocument(encoded: string): EncryptedPayload | null {
  if (!encoded.startsWith(ENCODED_DOCUMENT_PREFIX)) return null;
  const rest = encoded.slice(ENCODED_DOCUMENT_PREFIX.length);
  const sepIdx = rest.indexOf(':');
  if (sepIdx < 0) return null;
  try {
    return {
      iv:         base64ToBytes(rest.slice(0, sepIdx)),
      ciphertext: base64ToBytes(rest.slice(sepIdx + 1)),
    };
  } catch {
    return null; // Malformed base64 — treat as "not our format" rather than throwing.
  }
}
